import React from "react"
import { useDispatch, useSelector } from "react-redux"

import { useDropzone } from "react-dropzone"

import { masterRecordsSlice } from "@/slice/masterRecordsSlice"
import { masterSchemaSlice, useColumnTypes } from "@/slice/masterSchemaSlice"
import { uiSlice } from "@/slice/uiSlice"
import { RootState } from "@/store"
import * as types from "@/type"
import { gqlClient } from "@/util/graphql"
import { read } from "@/util/upload"

import { DroppedFileConfirmationModal } from "./DroppedFileConfirmationModal"
import { DropZoneModal } from "./DropZoneModal"

export const useDropZone = (tableName?: string) => {
	const dispatch = useDispatch()

	const [uploadFile, setUploadFile] = React.useState<types.UploadFile | null>(null)

	const [isUploading, setIsUploading] = React.useState<boolean>(false)

	const columnTypes = useColumnTypes()[tableName ?? ""]
	const onCloseUploadConfirmModal = React.useCallback(
		(result: boolean) => {
			if (result && uploadFile && tableName) {
				setIsUploading(true)
				gqlClient()
					.createMasterRecordsCsv({
						tableName,
						dataUri: uploadFile.data,
						fileName: uploadFile.fileName,
					})
					.then(({ createMasterRecordsCsv: { records, duplicateRecordIds } }) => {
						setIsUploading(false)
						setUploadFile(null)
						dispatch(masterRecordsSlice.actions.setData({ tableName, data: records, columnTypes, additional: true }))
						dispatch(masterRecordsSlice.actions.resetFunction({ tableName }))
						if (duplicateRecordIds) {
							dispatch(masterRecordsSlice.actions.deleteRecords({ tableName, recordIds: duplicateRecordIds }))
						}

						// header の並び順が変わるので改めて masterSchema を引き直す
						gqlClient()
							.masterSchema()
							.then((res) => {
								dispatch(masterSchemaSlice.actions.setData(res))
							})
					})
			} else if (!isUploading) {
				setUploadFile(null)
			} else {
				/** 現在、アップロード中なのでモーダルを閉じない */
			}
		},
		[dispatch, tableName, uploadFile, columnTypes, isUploading],
	)

	const onDrop = React.useCallback(
		async (acceptedFiles) => {
			if (tableName) {
				const file = await read(acceptedFiles[0], tableName)
				setUploadFile(file)
			}
		},
		[tableName],
	)

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		noClick: true,
		multiple: false,
		onDrop: onDrop,
	})

	const openFilePicker = useSelector((state: RootState) => state.ui.openFilePicker)
	React.useEffect(() => {
		if (openFilePicker) {
			open()
			dispatch(uiSlice.actions.setOpenFilePicker(false))
		}
	}, [openFilePicker, open, dispatch])

	const DropZone = (
		<>
			<DropZoneModal isDragActive={isDragActive} />
			<DroppedFileConfirmationModal
				isOpen={uploadFile != null}
				onClose={onCloseUploadConfirmModal}
				uploadFile={uploadFile}
				isButtonLoading={isUploading}
			/>
			<input {...getInputProps()} />
		</>
	)

	return {
		DropZone,
		getRootProps,
	}
}
